import { useCallback } from "react";
import { PiMagnifyingGlassBold } from "react-icons/pi";

import { Button, Flex, Input, InputGroup } from "@chakra-ui/react";

import { GeographicalAgencyType } from "@/codegen/openapi2";
import { MultiAgencySelector, MultiLegalEntitySelector } from "@/components/2/data-selectors";
import { MultiSelectDropdown } from "@/components/2/multiselect-dropdown";
import { useGetPostalCodesGeographiesPostalCodesGet } from "@/services/2/react-query/hooks";

import { PostalCodesFiltersType } from "./types";

type PostalCodesFiltersProps = {
  setFilters: (filters: PostalCodesFiltersType) => void;
  filters: PostalCodesFiltersType;
};

const GeographicalAgencyTypeOptions = [
  { label: "Coeur Zone", value: "core_agency" },
  { label: "Proche Zone", value: "close_core_agency" },
  { label: "Hors zone", value: "non_core_agency" },
];

export const PostalCodesFilters = ({ filters, setFilters }: PostalCodesFiltersProps) => {
  const resetFilters = useCallback(() => {
    setFilters({
      excludeUnboundPostalCodes: true,
    });
  }, [setFilters]);

  // Get global datas that will be filtered later
  const { data: cities } = useGetPostalCodesGeographiesPostalCodesGet({}, [], {
    staleTime: 12 * 60 * 60 * 1000, // 12 hours
  });

  const filteredCitiesName = !filters.postalCodes?.length
    ? cities
    : cities?.filter(city => {
        return filters.postalCodes?.length && filters.postalCodes.includes(city.postal_code);
      });
  const filteredPostalCodes = !filters.cityNames?.length
    ? cities
    : cities?.filter(city => {
        return filters.cityNames?.length && filters.cityNames.includes(city.city_name);
      });
  // Create a set of city names and postal codes to avoid duplicates
  const uniqueCityNames = Array.from(new Set(filteredCitiesName?.map(city => city.city_name)));
  const uniquePostalCodes = Array.from(new Set(filteredPostalCodes?.map(city => city.postal_code)));

  // Check if any filter is applied to enable the reset button (ignore excludeUnboundPostalCodes)
  const enableResetButton = () =>
    Object.values(filters).some(filter => filter && Array.isArray(filter) && filter.length > 0);

  return (
    <Flex justifyContent={"space-between"} alignItems={"center"}>
      <Flex gap={2}>
        <MultiLegalEntitySelector
          entities={filters.entities || []}
          onChange={newValues => setFilters({ ...filters, entities: newValues })}
        />

        <MultiSelectDropdown
          name="postalCodes"
          label="Code postal"
          pluralLabel="Codes postaux"
          options={uniquePostalCodes.map(postalCode => ({
            value: postalCode,
            label: postalCode,
          }))}
          values={filters.postalCodes || []}
          onChangeValues={newValues => setFilters({ ...filters, postalCodes: newValues })}
        />

        <MultiSelectDropdown
          name="cityNames"
          label="Ville"
          pluralLabel="Villes"
          options={uniqueCityNames.map(city => ({
            value: city,
            label: city,
          }))}
          values={filters.cityNames || []}
          onChangeValues={newValues => setFilters({ ...filters, cityNames: newValues })}
        />

        <MultiAgencySelector
          selectedAgencies={filters.agencies || []}
          onChange={newValues => setFilters({ ...filters, agencies: newValues })}
        />

        <MultiSelectDropdown
          name="geographicalAgencyTypes"
          label="Intervention"
          pluralLabel="Interventions"
          options={GeographicalAgencyTypeOptions.map(geographicalAgencyType => ({
            value: geographicalAgencyType.value,
            label: geographicalAgencyType.label,
          }))}
          values={filters.geographicalAgencyTypes || []}
          onChangeValues={newValues =>
            setFilters({
              ...filters,
              geographicalAgencyTypes: newValues as GeographicalAgencyType[],
            })
          }
        />
        {enableResetButton() && (
          <Button variant="plain" color={"blue"} onClick={resetFilters}>
            Effacer les filtres
          </Button>
        )}
      </Flex>

      <InputGroup maxW="sm" startElement={<PiMagnifyingGlassBold />}>
        <Input
          placeholder="Rechercher un code postal ou une ville"
          value={filters.globalSearch}
          onChange={e => setFilters({ ...filters, globalSearch: e.target.value })}
        />
      </InputGroup>
    </Flex>
  );
};
